import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, CalendarPlusIcon, CheckIcon, StarIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Tabs } from '../ui/Tabs';
import { RecommendationPanel } from '../intelligence/RecommendationPanel';
import { useSocial } from '../../contexts/SocialContext';
import { dailyItems, happenings, myAchievements, myReputation, trending } from '../../data/social';
import type { HappeningItem } from '../../types/social';
const DAYS = ['Today', 'This week', 'Upcoming'] as const;

/**
 * The daily overview. The question it answers is "is there something here for
 * me right now" — not "how long can we keep you here".
 */
export function CampusToday() {
  const {
    communities,
    me,
    stories,
    markStorySeen
  } = useSocial();
  const [day, setDay] = React.useState<(typeof DAYS)[number]>('Today');
  const joined = communities.filter(community => community.joined);
  const suggestions = communities.filter(community => !community.joined).slice(0, 3).map(community => ({
    id: community.id,
    title: community.name,
    meta: `${community.members.toLocaleString()} members · ${community.scope}`,
    reason: community.category === 'Academic' ? 'Matches your department' : `Matches an interest you listed`,
    to: '/social',
    fallback: false
  }));
  const shown = happenings.filter(item => item.day === day);
  return <div className="space-y-5" data-id="element-4709" data-mp="27htdq4damjlh">
      <Panel title={`Good morning, ${me.name.split(' ')[0]}`} description="Your campus today. Everything here is something you can act on." data-id="element-4710" data-mp="27j4hw5anhtd4">
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4711" data-mp="2gihpesf7s7v7">
          {dailyItems.map(item => <li key={item.id} data-id="element-4712" data-mp="3mx8wxeyueqy7">
              <Link to={item.to} className={`block rounded-md border px-4 py-3.5 hover:border-brand-300 ${item.urgent ? 'border-warn-200 bg-warn-50' : 'border-line bg-canvas'}`} data-id="element-4713" data-mp="3n04tayv1l7kj">
                <span className="cz-num block text-h3 font-semibold text-ink" data-id="element-4714" data-mp="1nsh8h7kuli42">{item.count}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-body-sm text-ink-muted" data-id="element-4715" data-mp="21rgu06d2dgw0">
                  {item.urgent && <AlertTriangleIcon aria-hidden className="h-4 w-4 shrink-0 text-warn-700" data-id="element-4716" data-mp="1944bc7a0xt7z" />}
                  {item.label}
                </span>
              </Link>
            </li>)}
        </ul>
      </Panel>

      {stories.length > 0 && <Panel title="Campus stories" description="Posted in the last 24 hours. They expire on their own — nothing here is kept." data-id="element-4717" data-mp="1iw0l4flh37s1">
          <ul className="flex flex-wrap gap-3" data-id="element-4718" data-mp="qcl8q1jwwh6h">
            {stories.map(story => <li key={story.id} data-id="element-4719" data-mp="lfbn6wzn8pnx">
                <button type="button" onClick={() => markStorySeen(story.id)} className="w-40 rounded-md border border-line bg-canvas p-3 text-left hover:border-brand-300" data-id="element-4720" data-mp="le0fwn6zozs2">
                  <span aria-hidden className={`grid h-11 w-11 place-items-center rounded-full text-body font-semibold ${story.seen ? 'bg-canvas text-ink-muted ring-2 ring-line' : 'bg-brand-700 text-white ring-2 ring-brand-300'}`} data-id="element-4721" data-mp="2o1h09m20r4v1">
                    {story.author.initials}
                  </span>
                  <span className="mt-2 block text-body-sm font-medium text-ink" data-id="element-4722" data-mp="36r6pzuqxd0g3">
                    {story.author.name}
                  </span>
                  <span className="mt-0.5 block text-caption text-ink-subtle" data-id="element-4723" data-mp="1vct610zal55c">{story.caption}</span>
                  <span className="mt-1 block text-caption text-ink-subtle" data-id="element-4724" data-mp="20bcxti0khb5m">
                    {story.audience === 'campus' ? 'Whole campus' : 'Your connections'}
                  </span>
                </button>
              </li>)}
          </ul>
        </Panel>}

      <Panel title="What's happening" description="Real events, study groups and club meetings — the same records as Community and your calendar." data-id="element-4725" data-mp="25vg83q7l5jsj">
        <Tabs tabs={DAYS} value={day} onChange={setDay} label="Activity timeframe" data-id="element-4726" data-mp="21vpms20tvvve" />
        <ul className="mt-4 space-y-2.5" data-id="element-4727" data-mp="2yiu6lsqxmner">
          {shown.map(item => <HappeningRow key={item.id} item={item} data-id="element-4728" data-mp="2tksnx8xf4q3t" />)}
        </ul>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2" data-id="element-4729" data-mp="2pio04qtninp3">
        <Panel title="Trending on your campus" description="Derived from discussion volume, questions and searches on this campus — never from raw engagement." bodyClassName="p-0" data-id="element-4730" data-mp="2pljygg9evee2">
          <ul className="divide-y divide-line" data-id="element-4731" data-mp="1957b4cm1gm6d">
            {trending.map(topic => <li key={topic.id} data-id="element-4732" data-mp="197k039q7rwsx">
                <Link to={topic.to} className="block px-5 py-3.5 hover:bg-canvas sm:px-6" data-id="element-4733" data-mp="3ktc4frrlu1fd">
                  <p className="text-body font-semibold text-ink" data-id="element-4734" data-mp="3prvua39bjxd0">{topic.label}</p>
                  <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-4735" data-mp="3tro40jag89jy">{topic.signal}</p>
                  <p className="cz-num mt-0.5 text-caption text-ink-subtle" data-id="element-4736" data-mp="2fl8nexe63hu">
                    {topic.participants} students involved
                  </p>
                </Link>
              </li>)}
          </ul>
        </Panel>

        <div className="space-y-5" data-id="element-4737" data-mp="cekaadjqoyvc">
          <RecommendationPanel title="Communities you might join" description="Based on your department and the interests you listed. Manage those in Settings." items={suggestions} data-id="element-4738" data-mp="hbkhirsbs7o1" />
          <Panel title="Your standing" description="Separate dimensions. There is deliberately no single social score." bodyClassName="p-0" data-id="element-4739" data-mp="2j4ruvvodof7a">
            <ul className="divide-y divide-line" data-id="element-4740" data-mp="3qghhctmmiw1y">
              {myReputation.map(facet => <li key={facet.dimension} className="px-5 py-3 sm:px-6" data-id="element-4741" data-mp="3lhmy5qkqv5hb">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-id="element-4742" data-mp="3bi550snychr">
                    <span className="text-body font-medium text-ink" data-id="element-4743" data-mp="3ve9lcm0jm8mi">{facet.dimension}</span>
                    <span className="text-body text-ink" data-id="element-4744" data-mp="d8wksy6rix90">{facet.value}</span>
                  </div>
                  <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-4745" data-mp="8actsjdbav9s">{facet.basis}</p>
                </li>)}
            </ul>
          </Panel>
        </div>
      </div>

      <Panel title="Your participation" description="Earned from records of what you actually did. Nothing here is self-declared, and you choose what shows on your profile." bodyClassName="p-0" data-id="element-4746" data-mp="n0sm0i45b3hg">
        <ul className="divide-y divide-line" data-id="element-4747" data-mp="r12xubemco48">
          {myAchievements.map(achievement => <li key={achievement.id} className="px-5 py-4 sm:px-6" data-id="element-4748" data-mp="1aluvv0ioyejr">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-4749" data-mp="1akjrozlc34s4">
                <div className="min-w-0" data-id="element-4750" data-mp="1fjd2gk98khol">
                  <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-4751" data-mp="11mg5e12iy3z9">
                    <StarIcon aria-hidden className="h-5 w-5 shrink-0 text-brand-700" data-id="element-4752" data-mp="15mi72a89exej" />
                    {achievement.label}
                  </p>
                  <p className="mt-0.5 max-w-[72ch] text-body-sm text-ink" data-id="element-4753" data-mp="rtsaxcz0oc3p">{achievement.detail}</p>
                  <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-4754" data-mp="woof1bhj2px6">
                    Earned from: {achievement.earnedFrom}
                  </p>
                </div>
                <Badge tone={achievement.visible ? 'ok' : 'neutral'} data-id="element-4755" data-mp="mxk0gcvrl72v">
                  {achievement.visible ? 'On your campus profile' : 'Hidden'}
                </Badge>
              </div>
            </li>)}
        </ul>
      </Panel>

      {joined.length > 0 && <Panel title="From your communities" bodyClassName="p-0" data-id="element-4756" data-mp="tedfhwmscg8d">
          <ul className="divide-y divide-line" data-id="element-4757" data-mp="of0rh8k6h347">
            {joined.map(community => <li key={community.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3.5 sm:px-6" data-id="element-4758" data-mp="od7llvhe2mx9">
                <div className="min-w-0" data-id="element-4759" data-mp="fe2moo1x0rkk">
                  <p className="text-body font-medium text-ink" data-id="element-4760" data-mp="aezif5trepdn">{community.name}</p>
                  <p className="text-body-sm text-ink-muted" data-id="element-4761" data-mp="5oj3fhaaivp6">
                    {community.scope} · {community.activity}
                  </p>
                </div>
                {community.official && <Badge tone="brand" data-id="element-4762" data-mp="n3ehe4zs51i">Official</Badge>}
              </li>)}
          </ul>
        </Panel>}
    </div>;
}
function HappeningRow({
  item
}: {
  item: HappeningItem;
}) {
  const [attending, setAttending] = React.useState(item.attending);
  return <li className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5 rounded-md border border-line px-4 py-3" data-id="element-4763" data-mp="3ivuk4hytm3q5">
      <div className="flex min-w-0 items-start gap-4" data-id="element-4764" data-mp="1yfcwb8ihnbh5">
        <span className="cz-num w-20 shrink-0 text-body font-semibold text-ink" data-id="element-4765" data-mp="23as9rzcl35d1">{item.time}</span>
        <div className="min-w-0" data-id="element-4766" data-mp="27f00u36ikxef">
          <Link to={item.to} className="text-body font-semibold text-ink hover:underline" data-id="element-4767" data-mp="27bluykyn6628">
            {item.title}
          </Link>
          <p className="text-body-sm text-ink-muted" data-id="element-4768" data-mp="1fn2bqurpc07u">
            {item.host} · {item.place}
          </p>
          <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-4769" data-mp="1jnm4zn95mjop">{item.kind}</p>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-4770" data-mp="1omp5qvi3q3qv">
        {attending === 'going' ? <Badge tone="ok" icon={<CheckIcon className="h-4 w-4" data-id="element-4772" data-mp="1tep5ony68wng" />} data-id="element-4771" data-mp="1te72f7d3di61">
            You are going
          </Badge> : <>
            <Button variant="primary" size="sm" icon={<CalendarPlusIcon className="h-4 w-4" data-id="element-4774" data-mp="10w1rlshlehvv" />} onClick={() => setAttending('going')} data-id="element-4773" data-mp="vy9sadembthn">
              Going
            </Button>
            {attending !== 'interested' && <Button variant="ghost" size="sm" onClick={() => setAttending('interested')} data-id="element-4775" data-mp="h6c38sl5qh6r">
                Interested
              </Button>}
            {attending === 'interested' && <Badge plain data-id="element-4776" data-mp="r505oj0nsnpc">Interested</Badge>}
          </>}
      </div>
    </li>;
}